import styled from "styled-components"

/* ============================================================
   Design tokens (locais) — derivados do theme, usados só aqui
   ============================================================ */
const tokens = {
  surface: "#FFFFFF",
  surfaceMuted: "#F8FAFC",
  borderSubtle: "rgba(16, 24, 40, 0.08)",
  borderDefault: "#E2E8F0",
  textPrimary: "#0F172A",
  textSecondary: "#475569",
  textMuted: "#64748B",
  danger: "#DC2626",
  dangerSoft: "rgba(220, 38, 38, 0.10)",
  shadowSoft:
    "0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)",
  shadowButton: "0 4px 12px rgba(0, 97, 230, 0.24)",
  radiusCard: "16px",
  radiusInput: "10px",
  radiusButton: "12px",
}

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100svh;
`

export const Main = styled.div`
  display: flex;
  gap: 32px;
  margin: 32px 0;
  padding: 0 0 64px 0;

  @media (max-width: ${({ theme }) => theme.bp.medium}px) {
    flex-direction: column;
    gap: 24px;
    margin: 24px 0;
  }
`

export const EventResume = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  flex: 3;
  background: ${tokens.surface};
  box-shadow: ${tokens.shadowSoft};
  border: 1px solid ${tokens.borderSubtle};
  padding: 32px;
  border-radius: ${tokens.radiusCard};
  height: fit-content;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    padding: 24px 20px;
    gap: 32px;
  }
`

export const EventData = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fadeTop +
    theme.animations.durations.main +
    theme.animations.delays.main()}

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    display: none;
  }
`

export const EventName = styled.span`
  font-size: 24px;
  font-weight: bold;
  color: ${tokens.textPrimary};

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fadeLeft +
    theme.animations.durations.main +
    theme.animations.delays.main(2)}
`

export const PaymentData = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  opacity: 0;
  ${({ theme }) =>
    theme.animations.types.fadeTop +
    theme.animations.durations.main +
    theme.animations.delays.main(1)}
`

export const PaymentTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${tokens.textPrimary};
  margin: 0 0 4px 0;
  line-height: 1.2;
  letter-spacing: -0.01em;
`

export const Methods = styled.div`
  display: flex;
  gap: 48px;
  width: 100%;
`

export const MethodSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`

export const MethodCard = styled.button<{
  $active: boolean
  $disabled?: boolean
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 22px 20px;
  border-radius: 14px;
  border: 1.5px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.blue.main : tokens.borderDefault};
  background: ${({ $active }) =>
    $active ? "rgba(0, 97, 230, 0.04)" : tokens.surface};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  font-family: "Poppins";
  text-align: left;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  box-shadow: ${({ $active }) =>
    $active
      ? "0 0 0 4px rgba(0, 97, 230, 0.10), 0 1px 2px rgba(16, 24, 40, 0.04)"
      : "0 1px 2px rgba(16, 24, 40, 0.03)"};
  transition: border-color 180ms ease-out, background-color 180ms ease-out,
    transform 120ms ease-out, box-shadow 180ms ease-out;

  &:hover {
    ${({ $disabled, $active }) =>
      !$disabled &&
      !$active &&
      `
        border-color: rgba(0, 97, 230, 0.35);
        background: rgba(0, 97, 230, 0.02);
      `}
  }

  &:active {
    ${({ $disabled }) => !$disabled && `transform: scale(0.985);`}
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 97, 230, 0.22);
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    padding: 14px 16px;
    min-height: 56px;
    gap: 12px;
    border-radius: 12px;
    justify-content: flex-start;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const MethodIconWrap = styled.div<{ $active: boolean }>`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: transform 180ms ease-out;

  img {
    height: 26px;
    width: auto;
  }

  svg {
    color: ${({ $active, theme }) =>
      $active ? theme.colors.blue.main : tokens.textSecondary};
    transition: color 180ms ease-out;
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    width: 32px;
    height: 32px;

    img {
      height: 22px;
    }

    svg {
      width: 22px;
      height: 22px;
    }
  }
`

export const MethodInfo = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`

export const MethodName = styled.span<{ $active: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.blue.main : tokens.textPrimary};
  line-height: 1.2;
  letter-spacing: -0.005em;
  transition: color 180ms ease-out;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    font-size: 14.5px;
  }
`

/* Mantido para compat com imports antigos, mas agora não é renderizado */
export const MethodSub = styled.span`
  display: none;
`

export const MethodPixImage = styled.img<{ $active: boolean }>`
  height: 28px;
  width: auto;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    height: 22px;
  }
`

/* Label "Pix" — visível só no mobile (no desktop mantém logo centralizada) */
export const PixLabelMobileOnly = styled.span<{ $active: boolean }>`
  display: none;
  font-size: 14.5px;
  font-weight: 600;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.blue.main : "#0F172A"};
  line-height: 1.2;
  letter-spacing: -0.005em;
  transition: color 180ms ease-out;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    display: inline-flex;
    align-items: center;
  }
`

export const MethodCheck = styled.span<{ $active: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.blue.main};
  color: #fff;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: scale(${({ $active }) => ($active ? 1 : 0.7)});
  transition: opacity 180ms ease-out, transform 180ms ease-out;
  pointer-events: none;

  svg {
    width: 10px;
    height: 10px;
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    position: static;
    margin-left: auto;
    display: ${({ $active }) => ($active ? "flex" : "none")};
    transform: none;
    opacity: 1;
  }
`

/* ============================================================
   CardFormBlock — agora com collapse suave (grid-rows trick)
   ============================================================ */

export const CardFormCollapse = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: grid-template-rows 220ms ease-out, opacity 180ms ease-out;

  /* wrapper interno que permite clip da altura */
  & > div {
    overflow: hidden;
    min-height: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 120ms linear;
  }
`

export const CardFormBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 4px;

  & > span {
    font-size: 18px;
    font-weight: 600;
    color: ${tokens.textPrimary};
  }
`

export const CardFormHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: ${tokens.textPrimary};

  svg {
    color: ${({ theme }) => theme.colors.blue.main};
  }
`

// Method (legado — mantido para compat, não mais usado)

export const Method = styled.div<{ $checked: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-items: center;
  gap: 24px;
  width: 100%;
  border: 1px solid
    ${({ $checked, theme }) => ($checked ? theme.colors.blue.main : "#ccc")};
  border-radius: 6px;
  padding: 24px;
  cursor: pointer;
  transition: border-color 0.3s;
  overflow: hidden;

  & > img {
    width: 120px;
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    width: 100%;

    img {
      width: 84px;
    }
  }
`

export const MTitle = styled.div``

export const List = styled.div`
  display: flex;
  gap: 12px;
  max-width: 100%;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
`

export const CardItem = styled.a`
  z-index: 1;
  position: relative;
`

export const Recommended = styled.div<{ $visible?: boolean }>`
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  background-color: #111;
  color: #fff;
  text-align: center;
  padding: 0px;
  z-index: 2;
`

export const OrderResume = styled.div`
  width: 320px;
`

/* ============================================================
   Form
   ============================================================ */

export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

export const FormBlock = styled.div<{ $k: number }>`
  display: flex;
  flex-direction: column;
  gap: 28px;

  & > span {
    font-size: 18px;
    font-weight: 600;
    color: ${tokens.textPrimary};
  }

  opacity: 0;
  animation: fadeTop 240ms ease-out forwards;
  animation-delay: ${({ $k }) => `${$k * 80}ms`};

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

export const FormLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    gap: 32px;
  }
`

export const FormLine = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    flex-direction: column;
    gap: 28px;
  }
`

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  flex: 1;

  & .inputHelper {
    font-size: 12.5px;
    color: ${tokens.textMuted};
    margin-top: 6px;
    line-height: 1.4;
  }

  & .inputError {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: ${tokens.danger};
    margin-top: 6px;
    line-height: 1.4;

    svg {
      flex-shrink: 0;
    }
  }
`

export const Label = styled.label`
  position: relative;
  max-width: 100%;
  flex: 1;

  & > span {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 16px;
    color: ${tokens.textMuted};
    pointer-events: none;
    transition: transform 180ms ease-out, font-size 180ms ease-out,
      color 180ms ease-out, background-color 180ms ease-out;
    background: transparent;
    padding: 0 4px;
  }

  input:focus + span,
  input:not(:placeholder-shown) + span {
    transform: translateY(calc(-50% - 26px));
    font-size: 12px;
    font-weight: 600;
    color: ${tokens.textSecondary};
    background: ${tokens.surface};
  }

  input:focus + span {
    color: ${({ theme }) => theme.colors.blue.main};
  }

  @media (prefers-reduced-motion: reduce) {
    & > span {
      transition: none;
    }
  }
`

export const DisclaimerArea = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.blue.main};
  padding: 12px 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blue.main};
  border-radius: 4px;
  width: 100%;
  transition: border-color 0.3s;
  box-sizing: border-box;
`

export const Input = styled.input<{ $error?: boolean }>`
  border: 1.5px solid
    ${({ $error }) => ($error ? tokens.danger : tokens.borderDefault)};
  outline: none;
  padding: 0 14px;
  height: 52px;
  font-size: 16px;
  color: ${tokens.textPrimary};
  border-radius: ${tokens.radiusInput};
  width: 100%;
  background: ${tokens.surface};
  font-family: "Poppins";
  transition: border-color 180ms ease-out, box-shadow 180ms ease-out,
    background-color 180ms ease-out;
  box-sizing: border-box;
  box-shadow: ${({ $error }) =>
    $error ? `0 0 0 4px ${tokens.dangerSoft}` : "none"};

  &:focus {
    border-color: ${({ $error, theme }) =>
      $error ? tokens.danger : theme.colors.blue.main};
    box-shadow: ${({ $error }) =>
      $error
        ? `0 0 0 4px ${tokens.dangerSoft}`
        : `0 0 0 4px rgba(0, 97, 230, 0.12)`};
  }

  &::placeholder {
    color: transparent;
  }

  &:disabled {
    background: ${tokens.surfaceMuted};
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const Checkbox = styled.label`
  cursor: pointer;
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${tokens.textSecondary};

  input {
    transform: translateY(1px) scale(1.15);
    accent-color: ${({ theme }) => theme.colors.blue.main};
    cursor: pointer;
  }

  &:has(.terms) {
    align-items: flex-start;
  }

  div.terms {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;

    span,
    a {
      white-space: nowrap;
    }

    a {
      color: ${({ theme }) => theme.colors.blue.main};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`

export const TicketBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background: ${tokens.surfaceMuted};
  border: 1px solid ${tokens.borderSubtle};
  border-radius: 12px;
`

export const TicketName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;

  & > span:first-child {
    color: ${tokens.textMuted};
    font-weight: 500;
  }

  & > span:last-child {
    color: ${tokens.textPrimary};
    font-weight: 600;
  }
`

export const CardNumberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  & > div:first-child {
    flex: 1;
  }

  img {
    height: 28px;
    width: auto;
    flex-shrink: 0;
  }
`


export const Button = styled.button<{ $disabled: boolean }>`
  outline: none;
  background-color: ${({ $disabled, theme }) =>
    !$disabled ? theme.colors.blue.main : "#E2E8F0"};
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  padding: 0 24px;
  height: 52px;
  border-radius: ${tokens.radiusButton};
  color: ${({ $disabled }) => ($disabled ? "#94A3B8" : "#fff")};
  width: 100%;
  align-self: stretch;
  font-size: 16px;
  font-weight: 600;
  font-family: "Poppins";
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background-color 180ms ease-out, box-shadow 180ms ease-out,
    transform 120ms ease-out;
  box-shadow: none;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      `
        background-color: #0052C2;
        box-shadow: ${tokens.shadowButton};
      `}
  }

  &:active {
    ${({ $disabled }) => !$disabled && `transform: scale(0.99);`}
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 97, 230, 0.28);
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    height: 56px;
    font-size: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
